import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Grid, InputAdornment, Box, CircularProgress } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import PropTypes from 'prop-types';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { message } from 'antd';
import MaterialPhoneInput from '../utils/MaterialPhoneInput';

const validationSchema = Yup.object().shape({
  clientName: Yup.string().required('El nombre es obligatorio'),
  clientEmail: Yup.string().required('El correo es obligatorio').email('El correo debe ser válido'),
  phoneNumber: Yup.string().required('El teléfono es obligatorio').matches(/^\+\d{10,15}$/, 'El teléfono debe tener un formato válido internacional'),
  cellphoneNumber: Yup.string().required('El celular es obligatorio').matches(/^\+\d{10,15}$/, 'El celular debe tener un formato válido internacional'),
});

function NewClientDialog({ openDiagTwo, handleCloseTwo, handleCancelTwo, handleClientCreation }) {
  const initialValues = { clientName: '', phoneNumber: '+1809', cellphoneNumber: '+1829', clientEmail: '', address: '' };
  const [messageApi, contextHolder] = message.useMessage();

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/clients/create-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al registrar el cliente');
      }
      const data = await response.json();
      console.log('Cliente registrado:', data);
      values.clientID = data.clientID; // Asignar el ID del cliente a los valores
      handleClientCreation(values); // Llamar a la función de creación de cliente con los valores
      // Mostrar notificación de éxito
      messageApi.success({
        content: 'Cliente registrado exitosamente',
        duration: 2,
        style: {
          marginTop: '20vh',
          backgroundColor: '#dff9fb',
          color: '#2ed573',
          fontWeight: 600,
          fontSize: '1rem',
        },
      });
      resetForm();
      handleCloseTwo(); // Cerrar el diálogo después de registrar el cliente
    } catch (error) {
      console.error('Error al registrar cliente:', error);
      setNotification({
        open: true,
        message: error.message || 'Error al registrar el cliente',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={openDiagTwo} onClose={handleCloseTwo} fullWidth maxWidth="sm" sx={{ borderRadius: '12px' }}>
      {contextHolder}
      <DialogTitle sx={{
        bgcolor: '#f5f6fa',
        color: '#2d3436',
        fontWeight: 700,
        fontSize: '1.5rem',
        borderBottom: '1px solid #e0e0e0',
        py: 2
      }}>
        <PersonAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Nuevo Cliente
      </DialogTitle>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting }) => (
          <Form>
            <DialogContent sx={{ py: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field name="clientName">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Nombre completo"
                        variant="outlined"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <ErrorOutlineIcon sx={{ color: '#ff5252', fontSize: 16, mr: 0.5 }} />
                            <span style={{ color: '#ff5252', fontSize: '0.8rem' }}>{meta.error}</span>
                          </Box>
                        )}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlineIcon sx={{ color: '#636e72' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '&:hover fieldset': { borderColor: '#74b9ff' },
                            '&.Mui-focused fieldset': { borderColor: '#0984e3' }
                          }
                        }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name="clientEmail">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Correo electrónico"
                        type="email"
                        variant="outlined"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ErrorOutlineIcon sx={{ color: '#ff5252', fontSize: 16, mr: 0.5 }} />
                            <span style={{ color: '#ff5252', fontSize: '0.8rem' }}>{meta.error}</span>
                          </Box>
                        )}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: '#636e72' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '&:hover fieldset': { borderColor: '#74b9ff' }
                          }
                        }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field name="phoneNumber">
                    {({ field, form, meta }) => (
                      <MaterialPhoneInput
                        name={field.name}
                        value={field.value}
                        label="Teléfono"
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                        defaultCountry="do"
                        onChange={(phone) => {
                          form.setFieldValue(field.name, phone);
                        }}
                        labelFontSize="0.875rem"
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field name="cellphoneNumber">
                    {({ field, form, meta }) => (
                      <MaterialPhoneInput
                        name={field.name}
                        value={field.value}
                        label="Celular"
                        error={meta.touched && !!meta.error}
                        color={meta.error ? 'error' : (!meta.error && meta.touched ? 'success' : undefined)}
                        helperText={meta.touched && meta.error && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ErrorOutlineIcon sx={{ color: '#ff5252', fontSize: 16, mr: 0.5 }} />
                            <span style={{ color: '#ff5252', fontSize: '0.8rem' }}>{meta.error}</span>
                          </Box>
                        )}
                        onChange={(phone) => {
                          form.setFieldValue(field.name, phone);
                        }}
                        defaultCountry="do"
                        labelFontSize="0.875rem"
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name="address">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Dirección"
                        variant="outlined"
                        multiline
                        rows={2}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <HomeIcon sx={{ color: '#636e72' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '&:hover fieldset': { borderColor: '#74b9ff' }
                          }
                        }}
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                onClick={handleCancelTwo}
                variant="outlined"
                sx={{
                  color: '#e74c3c',
                  borderColor: '#e74c3c',
                  borderRadius: '8px',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#fcebec',
                    borderColor: '#e74c3c'
                  }
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  backgroundColor: '#0984e3',
                  borderRadius: '8px',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#0873c4'
                  },
                  '&:disabled': {
                    backgroundColor: '#dfe6e9',
                    color: '#b2bec3'
                  }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : (
                  'Registrar'
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default NewClientDialog;